<template>
	<div v-if="isLaneMounted">
		<div
			class="moments-lane l-content-section"
		>
			<div class="moments-lane__row">
				<div class="moments-lane__title-vertical">
					{{ $I18n.trans_choice('commons.moment', 2).toLowerCase() }}
				</div>
				<div class="moments-lane__carousel">
					<Carousel
						:cells="sortedItems"
						:group-cells="'100%'"
					>
						<template #cell="{ cell, isDragging }">
							<a
								:key="cell.id"
								class="moments-lane__cell moments-lane__group"
								:class="{ '-highlighted': isHighlighted(cell) }"
								href="#"
								@click.prevent="openGroup(cell, isDragging)"
							>
								<div class="moments-lane__group-inner">
									<img
										:src="cell.participants?.[0]?.imageUrl"
										:alt="cell.participants?.[0]?.clubCode || ''"
										class="moments-lane__club-logo -top"
									>
									<img
										:src="cell.participants?.[1]?.imageUrl"
										:alt="cell.participants?.[1]?.clubCode || ''"
										class="moments-lane__club-logo -bottom"
									>
									<div class="moments-lane__tags">
										<div class="moments-lane__tag -top">
											{{ cell.participants?.[0]?.clubCode || '' }}
										</div>
										<div class="moments-lane__tag -bottom">
											{{ cell.participants?.[1]?.clubCode || '' }}
										</div>
									</div>
								</div>
							</a>
						</template>
					</Carousel>
				</div>
			</div>
		</div>
		<div class="moments-lane__hidden-player">
			<MomentsStoriesPlayer
				ref="storiesPlayer"
				:items="sortedItems"
				@group-complete="handleGroupComplete"
				@availability-changed="handlePlayerAvailability"
			/>
		</div>
	</div>
</template>

<script>
import Carousel from '@js/components/Carousel';
import MomentsStoriesPlayer from '@js/components/MomentsStoriesPlayer';

export default {
	name:       'MomentsLane',
	components: { Carousel, MomentsStoriesPlayer },
	props:      {
		items: {
			type:    Array,
			default: () => [],
		},
	},
	data() {
		return {
			watchedGroupsById: {},
			watchStateLoaded:  false,
			isPlayerAvailable: false,
			isLaneMounted:     true,
		};
	},
	computed: {
		sortedItems() {
			return [...this.items].sort((a, b) => new Date(b.lastUpdatedAt).getTime() - new Date(a.lastUpdatedAt).getTime());
		},
		storageKey() {
			return `arena:moments:watch:v1:${this.$arenaConfig.domainKey}:${this.$arenaConfig.id}`;
		},
	},
	watch: {
		items: {
			handler() {
				this.loadWatchedGroups();
			},
			deep: true,
		},
	},
	mounted() {
		this.loadWatchedGroups();
	},
	methods: {
		loadWatchedGroups() {
			try {
				const parsed = JSON.parse(window.localStorage.getItem(this.storageKey) || '{}');
				this.watchedGroupsById = typeof parsed === 'object' && parsed ? parsed : {};
			} catch {
				this.watchedGroupsById = {};
			} finally {
				this.watchStateLoaded = true;
			}
		},
		saveWatchedGroups() {
			window.localStorage.setItem(this.storageKey, JSON.stringify(this.watchedGroupsById));
		},
		getMomentIds(group) {
			return (group.moments || []).map(moment => moment.id);
		},
		hasNewVideo(group, watchedGroup) {
			const watchedMomentIds = Array.isArray(watchedGroup?.seenMomentIds) ? watchedGroup.seenMomentIds : [];
			const groupMomentIds = this.getMomentIds(group);
			const hasNewMomentId = groupMomentIds.some(momentId => !watchedMomentIds.includes(momentId));
			const hasNewTimestamp = !watchedGroup?.lastSeenUpdatedAt ||
				new Date(group.lastUpdatedAt).getTime() > new Date(watchedGroup.lastSeenUpdatedAt).getTime();

			return hasNewMomentId || hasNewTimestamp;
		},
		isHighlighted(group) {
			if (!this.watchStateLoaded) {
				return false;
			}

			const watchedGroup = this.watchedGroupsById[group.id];
			if (!watchedGroup) {
				return true;
			}

			return this.hasNewVideo(group, watchedGroup);
		},
		openGroup(group, isDragging) {
			if (isDragging || !this.isPlayerAvailable) {
				return;
			}

			this.$refs.storiesPlayer?.openStory(group.id);
		},
		handlePlayerAvailability(isAvailable) {
			this.isPlayerAvailable = isAvailable;
			if (!isAvailable) {
				this.isLaneMounted = false;
			}
		},
		handleGroupComplete(groupId) {
			const group = this.sortedItems.find(item => item.id === groupId);
			if (!group) {
				return;
			}

			this.watchedGroupsById = {
				...this.watchedGroupsById,
				[group.id]: {
					lastSeenUpdatedAt: group.lastUpdatedAt,
					seenMomentIds:     this.getMomentIds(group),
					watchedAt:         new Date().toISOString(),
				},
			};

			this.saveWatchedGroups();
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.moments-lane {
	--moments-lane-angle: -8.6deg;
	position: relative;

	&::after {
		content: '';
		display: block;
		position: absolute;
		top: -28px;
		left: 44px;
		width: calc(100% - 44px);
		height: calc(100% + 56px);
		background-color: var(--color-grey-950);
		z-index: -1;
		border-radius: var(--t-border-radius-card--lg);
	}
}

.moments-lane__hidden-player {
	display: none;
}

.moments-lane__row {
	display: flex;
	align-items: center;
	gap: $sp4;
}

.moments-lane__title-vertical {
	writing-mode: vertical-rl;
	transform: rotate(180deg);
	font-weight: 300; // stylelint-disable-line
	font-size: 28px; // stylelint-disable-line
	text-transform: lowercase;
	letter-spacing: -1px; // stylelint-disable-line
	flex-shrink: 0;
	line-height: 1;
	width: 50px;

	@include size(md-up) {
		font-size: 32px; // stylelint-disable-line
	}
}

.moments-lane__carousel {
	width: 100%;
	min-width: 0;
}

.moments-lane__cell {
	display: block;
	width: calc(102px + (2 * #{$carousel-card-overflow}));
	height: calc(152px + (2 * #{$carousel-card-overflow}));
	padding: $carousel-card-overflow;
}

.moments-lane__group {
	display: inline-block;
}

.moments-lane__group-inner {
	position: relative;
	width: 99px;
	height: 152px;
	border-radius: var(--t-border-radius-card--lg);
	overflow: hidden;
	outline: 2px solid transparent;
	outline-offset: 2px;
	transition: transform $trans-time-fast, border-color $trans-time-fast, filter $trans-time-fast;
	will-change: transform, border-color;
	cursor: pointer;
	transform-origin: center center;

	&::before {
		content: '';
		position: absolute;
		inset: 0 auto auto 0;
		width: 99px;
		height: 83px;
		background-color: var(--color-black);
	}

	&::after {
		content: '';
		position: absolute;
		left: 0;
		top: 50%;
		width: 99px;
		height: 85px;
		background-color: var(--color-grey-600);
		transform: skew(0deg, var(--moments-lane-angle));
	}

	.moments-lane__group.-highlighted & {
		outline-color: var(--color-hype-yellow);
	}

	@media (hover:hover) {
		.moments-lane__group:hover & {
			transform: scale(1.09);
		}
	}
}

.moments-lane__club-logo {
	--logo-size: 30px;
	--logo-h-offset: 7px;
	--logo-v-offset: 7px;
	position: absolute;
	object-fit: contain;
	z-index: 2;

	&.-top {
		width: var(--logo-size);
		height: var(--logo-size);
		left: var(--logo-h-offset);
		top: var(--logo-v-offset);
	}

	&.-bottom {
		width: var(--logo-size);
		height: var(--logo-size);
		right: var(--logo-h-offset);
		bottom: var(--logo-v-offset);
	}
}

.moments-lane__tags {
	position: relative;
	z-index: 2;
	width: 100%;
	height: 100%;
}

.moments-lane__tag {
	position: absolute;
	display: flex;
	align-items: center;
	font-size: 32px; // stylelint-disable-line
	letter-spacing: -1px; // stylelint-disable-line
	line-height: 1;
	white-space: nowrap;
	transform-origin: center;

	&.-top {
		left: 0;
		bottom: calc(50% - 0.02em); // optical correction for line height
		font-weight: 800; // stylelint-disable-line
		color: var(--color-grey-600);
		transform: rotate(var(--moments-lane-angle));
	}

	&.-bottom {
		right: 0;
		top: calc(50%);
		font-weight: 400; // stylelint-disable-line
		color: var(--color-black);
		transform: rotate(calc(var(--moments-lane-angle)));
	}
}
</style>
